/* eslint-disable no-underscore-dangle */
import {
  AddressFieldsOptions,
  useAddressFieldsForm,
} from '@graphcommerce/magento-customer/components/AddressFields/useAddressFieldsForm'
import { InputCheckmark } from '@graphcommerce/next-ui'
import { FieldValues, FieldPath } from '@graphcommerce/react-hook-form'
import { Trans } from '@lingui/react'
import { Alert, Box, TextField } from '@mui/material'
import Script from 'next/script'
import { useEffect, useRef, useState, useCallback, memo } from 'react'
import { AddressSummary } from './AddressSummary'

let autocompleteInstance: google.maps.places.Autocomplete | null = null
let autocompleteListener: google.maps.MapsEventListener | null = null

export interface AddressGoogleAutoCompleteProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends AddressFieldsOptions<TFieldValues, TName> {
  setAutocompleteSuccess: (success: boolean) => void
  autocompleteSuccess: boolean
}

function AddressGoogleAutoCompleteBase<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: AddressGoogleAutoCompleteProps<TFieldValues, TName>) {
  const { setAutocompleteSuccess, autocompleteSuccess, ...restProps } = props
  const form = useAddressFieldsForm<TFieldValues, TName>(restProps)
  const { name, readOnly, watch } = form
  const country = watch(name.countryCode)
  const [houseNumberValid, setHouseNumberValid] = useState<boolean | null>(null)
  const addressFieldHasBeenFocusedRef = useRef<boolean>(false)
  const [showField, setShowField] = useState<boolean>(true)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)

  // Get up-to-date valid and values from form (important for async updating after place selection)
  const streetValid = watch(name.street)
  const houseNumberValidField = watch(name.houseNumber)
  const cityValid = watch(name.city)
  const postcodeValid = watch(name.postcode)
  const isValidAddress = !!streetValid && !!houseNumberValidField && !!cityValid && !!postcodeValid

  const mapPlaceToFields = useCallback(
    (place: google.maps.places.PlaceResult) => {
      if (!place.address_components) return

      const addressComponents = place.address_components
      const streetNumber =
        addressComponents.find((comp) => comp.types.includes('street_number'))?.long_name || ''
      const route = addressComponents.find((comp) => comp.types.includes('route'))?.long_name || ''
      const city =
        addressComponents.find((comp) => comp.types.includes('locality'))?.long_name || ''
      const state =
        addressComponents.find((comp) => comp.types.includes('administrative_area_level_1'))
          ?.short_name || ''
      const zip =
        addressComponents.find((comp) => comp.types.includes('postal_code'))?.long_name || ''

      const setFormValue = (field: keyof typeof name, value: string) => {
        form.setValue(name[field], value as TFieldValues[TName], {
          shouldValidate: true,
          shouldDirty: true,
        })
      }

      setFormValue('street', route)
      setFormValue('houseNumber', streetNumber)
      setFormValue('city', city)
      setFormValue('regionId', state)
      setFormValue('postcode', zip)
    },
    [form, name],
  )

  // If there is an autosuggest option available, and the user does not select it, but presses 'Enter' on the input instead,
  // we simulate a down key action to effectively select the first autosuggest option
  function enableEnterKey(input: HTMLInputElement) {
    // Prevent double-wrapping
    // @ts-expect-error we're setting this ourselves
    if (input._enterKeyListenerPatched) return
    // @ts-expect-error we're setting this ourselves
    input._enterKeyListenerPatched = true

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const _addEventListener = input.addEventListener
    const addEventListenerWrapper = (type: string, listener: (event: Event) => void) => {
      if (type === 'keydown') {
        const _listener = listener
        // eslint-disable-next-line no-param-reassign
        listener = (event: Event) => {
          if (event instanceof KeyboardEvent) {
            const suggestionSelected = document.getElementsByClassName('pac-item-selected').length
            const suggestionExists = document.getElementsByClassName('pac-item').length
            if (
              (event.key === 'Enter' || event.key === 'Tab') &&
              !suggestionSelected &&
              suggestionExists
            ) {
              // simulate down arrow, then original event
              const e = new KeyboardEvent('keydown', {
                key: 'ArrowDown',
                code: 'ArrowDown',
                keyCode: 40,
                bubbles: true,
              })
              _listener(e as Event)
            }
          }
          _listener(event)
        }
      }
      setTimeout(() => {
        _addEventListener.call(input, type, listener)
      })
    }

    input.addEventListener = addEventListenerWrapper
  }

  const autocompleteInputRef = useCallback((node: HTMLInputElement | null) => {
    if (!node) return
    // enableEnterKey is defined below
    enableEnterKey(node)
  }, [])

  const cleanupAutocomplete = useCallback(() => {
    if (autocompleteListener) {
      google.maps.event.removeListener(autocompleteListener)
      autocompleteListener = null
    }

    if (autocompleteInstance) {
      google.maps.event.clearInstanceListeners(autocompleteInstance)
      autocompleteInstance = null
    }

    Array.from(document.querySelectorAll('.pac-container')).forEach((container) =>
      container.remove(),
    )
  }, [])

  const placeChangedListener = useCallback(() => {
    const place = autocompleteInstance?.getPlace()
    if (place && place.address_components) {
      mapPlaceToFields(place)
      setHouseNumberValid(
        Boolean(
          place.address_components.find((component) => component.types.includes('street_number')),
        ),
      )
      // Remove direct setAutocompleteSuccess(true) from here; let validation effect handle it!
    } else {
      setAutocompleteSuccess(false)
    }
  }, [mapPlaceToFields, setAutocompleteSuccess])

  useEffect(() => {
    const initAutocomplete = () => {
      if (!window.google?.maps?.places || autocompleteInstance) {
        return
      }

      cleanupAutocomplete()

      const inputNode = document.querySelector<HTMLInputElement>(
        'input[name="google-autocomplete"]',
      )
      if (!inputNode) return

      const sessionToken = new google.maps.places.AutocompleteSessionToken()
      autocompleteInstance = new window.google.maps.places.Autocomplete(inputNode, {
        types: ['address'],
        fields: ['address_components'],
        ...(country ? { componentRestrictions: { country } } : {}),
      })

      // @ts-expect-error: sessionToken is not in definitions but does exist.
      autocompleteInstance.setOptions({ sessionToken })
      autocompleteListener = autocompleteInstance.addListener('place_changed', placeChangedListener)

      // Only set data-1p-ignore here, not enableEnterKey
      inputNode.setAttribute('data-1p-ignore', '')
    }

    if (isScriptLoaded && typeof window.google !== 'undefined') {
      initAutocomplete()
    }

    return () => {
      cleanupAutocomplete()
    }
  }, [isScriptLoaded, country, placeChangedListener, cleanupAutocomplete])

  useEffect(() => {
    if (!showField) {
      window.document.body.querySelector('#google-autocomplete-container')?.parentElement?.remove()
    }
  }, [showField])

  // Handle showing/hiding AddressSummary at the right time (when valid after update)
  useEffect(() => {
    if (isValidAddress && showField) {
      setAutocompleteSuccess(true)
    }
  }, [isValidAddress, showField, setAutocompleteSuccess])

  return (
    <>
      <Script
        id='google-maps-script'
        src={`https://maps.googleapis.com/maps/api/js?key=${import.meta.graphCommerce.googleMapsApiKey}&libraries=places`}
        onLoad={() => setIsScriptLoaded(true)}
        strategy='lazyOnload'
      />
      <Box id='google-autocomplete-container'>
        <TextField
          variant='outlined'
          name='google-autocomplete'
          type='text'
          label={<Trans id='Address' />}
          inputRef={autocompleteInputRef}
          fullWidth
          autoComplete='new-password' // prevent autofill
          InputProps={{
            readOnly,
            endAdornment: <InputCheckmark show={isValidAddress} />,
          }}
          onChange={() => {
            // If the autofill feature is used, we cant reliably do event handling for the dropdown fields
            // But if the autofill user is used, the user is not very likely to make errors, so we disable the autocompplete feature
            if (addressFieldHasBeenFocusedRef.current === false) {
              setAutocompleteSuccess(false)
              setShowField(false)
            }
          }}
          onFocus={() => {
            addressFieldHasBeenFocusedRef.current = true

            // It's possible that due to an adblocker or cookie settings the maps api cant be loaded. If this is the case, revert back to manual fields
            if (typeof window.google?.maps === 'undefined') {
              setAutocompleteSuccess(false)
              setShowField(false)
            }

            // sadly this hack is required as Google's script sets autocomplete="off" on the input, which does not actually prevent autocomplete
            return document
              .querySelector('input[name="google-autocomplete"]')
              ?.setAttribute('autocomplete', 'new-password')
          }}
          onBlur={(e) => {
            setTimeout(() => {
              // If the user filled in a value, but no housenumber validation or success ActionCard is visible, revert back to the old form.
              // This can happen if the user enters his address and just completely ignores the autocomplete feature and clicks outside the field.
              if (
                e.target.value !== '' &&
                !document.querySelector('#validate-housenumber') &&
                !document.querySelector('#google-autocomplete-container .ActionCard-root')
              ) {
                setAutocompleteSuccess(false)
                setShowField(false)
              }
            }, 1000)
          }}
        />
        {houseNumberValid === false && (
          <Alert severity='error' id='validate-housenumber'>
            <Trans id='Please provide a housenumber' />
          </Alert>
        )}

        {isValidAddress && autocompleteSuccess && (
          <AddressSummary
            setAutocompleteSuccess={setAutocompleteSuccess}
            address={form.getValues()}
          />
        )}
      </Box>
    </>
  )
}

const AddressGoogleAutoComplete = memo(
  AddressGoogleAutoCompleteBase,
  (prevProps, nextProps) => prevProps?.name?.countryCode === nextProps?.name?.countryCode,
) as typeof AddressGoogleAutoCompleteBase

export { AddressGoogleAutoComplete }
